import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { 
  FaPhone, FaEnvelope, FaMapMarkerAlt, FaPaperPlane,
  FaInstagram, FaArrowRight, FaWhatsapp, FaFacebook, FaYoutube, FaTiktok
} from 'react-icons/fa';
import { useLanguage } from './LanguageProvider';
import { useDispatch } from 'react-redux';
import { Add_Client_Action } from '../Redux/Actions/ClientAction';
import { useNavigate, Link, useLocation } from 'react-router-dom';

export const Footer = () => {
  const { currentLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  const [phone, setPhone] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const footerRef = useRef(null);

  const isRTL = currentLang === 'ar';
  
  const translations = {
    EN: {
      tagline: "FlyVia helps you move, settle, and build your future in Spain and Europe with clear, practical support.",
      quickLinks: "Quick Links",
      home: "Home",
      aboutUs: "About Us",
      services: "Services",
      blog: "Blog",
      contact: "Contact",
      ourServices: "Our Services",
      residency: "Spain Residency",
      formation: "Corporate Setup",
      relocation: "Elite Relocation",
      aviation: "Private Jet Charter",
      properties: "Luxury Real Estate",
      concierge: "VIP Concierge",
      contactInfo: "Contact Info",
      address: "Rock Plaza, Fifth Settlement",
      phone: "+34 614 909 102",
      email: "info@flyvia.com",
      stayUpdated: "Stay Updated",
      newsletterText: "Get updates about Spain residency and relocation.",
      subscribe: "Subscribe",
      thankYou: "Thank you!",
      copyright: `© ${new Date().getFullYear()} FlyVia. All rights reserved.`,
      privacy: "Privacy Policy",
      terms: "Terms of Service"
    },
    AR: {
      tagline: "FlyVia تساعدك على الانتقال والاستقرار وبناء مستقبلك في إسبانيا وأوروبا بخطة واضحة ودعم عملي.",
      quickLinks: "روابط سريعة",
      home: "الرئيسية",
      aboutUs: "من نحن",
      services: "الخدمات",
      blog: "المدونة",
      contact: "تواصل معنا",
      ourServices: "خدماتنا",
      residency: "الإقامة في إسبانيا",
      formation: "تأسيس الشركات",
      relocation: "الانتقال المتميز",
      aviation: "طيران خاص",
      properties: "عقارات فاخرة",
      concierge: "كونسيرج VIP",
      contactInfo: "معلومات التواصل",
      address: "روك بلازا، التجمع الخامس",
      phone: "+34 614 909 102",
      email: "info@flyvia.com",
      stayUpdated: "ابقَ على اطلاع",
      newsletterText: "احصل على تحديثات حول الإقامة في إسبانيا والانتقال.",
      subscribe: "اشترك",
      thankYou: "شكرًا لك!",
      copyright: `© ${new Date().getFullYear()} FlyVia. جميع الحقوق محفوظة.`,
      privacy: "سياسة الخصوصية",
      terms: "شروط الخدمة"
    }
  };

  const t = (key) => translations[isRTL ? 'AR' : 'EN']?.[key] || translations.EN[key] || key;

  const handleNavigation = (path) => {
    window.scrollTo(0, 0);
    if (path.includes('#contact')) {
      if (location.pathname === '/') {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/');
        setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 300);
      }
    } else {
      navigate(path);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (phone.trim()) {
      await dispatch(Add_Client_Action({ phone, message: 'Newsletter Subscription' }));
      setSubscribed(true);
      setPhone('');
      setTimeout(() => setSubscribed(false), 3000);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold: 0.1 });
    if (footerRef.current) observer.observe(footerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <footer className="flyvia-footer mt-5 !rounded-tr-[50px] !rounded-tl-[50px]" ref={footerRef} dir={isRTL ? 'rtl' : 'ltr'}>
      <style>{`
        .flyvia-footer {
          background: #0f1218;
          color: #fff;
          padding: 100px 0 40px;
          font-family: var(--font-primary);
          position: relative;
          overflow: hidden;
        }
        .footer-logo {
          font-family: var(--font-heading);
          font-size: 32px;
          font-weight: 700;
          margin-bottom: 24px;
          display: block;
          text-decoration: none !important;
        }
        .footer-logo .softy { color: #fff; }
        .footer-logo .pinko { color: #ae0001; }
        .footer-tagline {
          color: rgba(255,255,255,0.5);
          line-height: 1.8;
          font-size: 14px;
          margin-bottom: 40px;
          max-width: 320px;
        }
        .footer-title {
          font-family: var(--font-heading);
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 30px;
          color: #fff;
          position: relative;
        }
        .footer-title::after {
          content: '';
          position: absolute;
          bottom: -10px;
          left: ${isRTL ? 'auto' : '0'};
          right: ${isRTL ? '0' : 'auto'};
          width: 30px;
          height: 2px;
          background: #ae0001;
        }
        .footer-links { list-style: none; padding: 0; margin: 0; }
        .footer-links li { margin-bottom: 15px; }
        .footer-links a {
          color: rgba(255,255,255,0.6);
          text-decoration: none !important;
          font-size: 14px;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          position: relative;
        }
        .footer-links a::before {
          content: '${isRTL ? '←' : '→'}';
          position: absolute;
          ${isRTL ? 'right' : 'left'}: -20px;
          color: #ae0001;
          opacity: 0;
          transition: all 0.3s ease;
          font-weight: bold;
        }
        .footer-links a:hover {
          color: #fff;
          padding-${isRTL ? 'right' : 'left'}: 15px;
        }
        .footer-links a:hover::before {
          opacity: 1;
          ${isRTL ? 'right' : 'left'}: 0;
        }
        .contact-item {
          display: flex;
          gap: 15px;
          margin-bottom: 20px;
          color: rgba(255,255,255,0.6);
          font-size: 14px;
        }
        .contact-item svg { color: #ae0001; margin-top: 4px; }
        .social-links { display: flex; gap: 15px; margin-top: 30px; }
        .social-link {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(255,255,255,0.05);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          transition: 0.3s;
          text-decoration: none !important;
        }
        .social-link:hover { background: #ae0001; transform: translateY(-5px); }
        .newsletter-form { position: relative; margin-top: 20px; }
        .newsletter-input {
          width: 100%;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          padding: 15px 20px;
          border-radius: 12px;
          color: #fff;
          font-size: 14px;
          outline: none;
        }
        .newsletter-btn {
          position: absolute;
          top: 5px;
          left: ${isRTL ? '5px' : 'auto'};
          right: ${isRTL ? 'auto' : '5px'};
          background: #ae0001;
          color: #fff;
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .footer-bottom {
          margin-top: 80px;
          padding-top: 30px;
          border-top: 1px solid rgba(255,255,255,0.05);
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
        }
        .copyright { color: rgba(255,255,255,0.4); font-size: 13px; }
        .legal-links { display: flex; gap: 20px; }
        .legal-links a { color: rgba(255,255,255,0.4); text-decoration: none; font-size: 13px; }
      `}</style>
      
      <Container>
        <Row className="gy-5">
          <Col lg={4}>
            <Link to="/" className="footer-logo">
              <span className="softy">FLY</span><span className="pinko">VIA</span>
            </Link>
            <p className="footer-tagline">{t('tagline')}</p>
            <div className="social-links">
              <a href="https://www.instagram.com/flyvia.services/" className="social-link"><FaInstagram /></a>
              <a href="https://www.facebook.com/profile.php?id=61588256249191" className="social-link"><FaFacebook /></a>
              <a href="https://www.youtube.com/@Flyvia-x8d/videos" className="social-link"><FaYoutube /></a>
              <a href="https://www.tiktok.com/@flyvia.services" className="social-link"><FaTiktok /></a>
              <a href="https://wa.me/34614909102" className="social-link"><FaWhatsapp /></a>
            </div>
          </Col>
          
          <Col md={4} lg={2}>
            <h4 className="footer-title">{t('quickLinks')}</h4>
            <ul className="footer-links">
              <li><a onClick={() => handleNavigation('/')}>{t('home')}</a></li>
              <li><a onClick={() => handleNavigation('/Aboutuspage')}>{t('aboutUs')}</a></li>
              <li><a onClick={() => handleNavigation('/ourservicepage')}>{t('services')}</a></li>
              <li><a onClick={() => handleNavigation('/MainBlogsCardspage')}>{t('blog')}</a></li>
              <li><a onClick={() => handleNavigation('/#contact')}>{t('contact')}</a></li>
            </ul>
          </Col>
          
          <Col md={4} lg={3}>
            <h4 className="footer-title">{t('ourServices')}</h4>
            <ul className="footer-links">
              <li><a onClick={() => handleNavigation('/ourservicepage')}>{t('residency')}</a></li>
              <li><a onClick={() => handleNavigation('/ourservicepage')}>{t('formation')}</a></li>
              <li><a onClick={() => handleNavigation('/ourservicepage')}>{t('relocation')}</a></li>
              <li><a onClick={() => handleNavigation('/ourservicepage')}>{t('aviation')}</a></li>
              <li><a onClick={() => handleNavigation('/ourservicepage')}>{t('properties')}</a></li>
              <li><a onClick={() => handleNavigation('/ourservicepage')}>{t('concierge')}</a></li>
            </ul>
          </Col>
          
          <Col md={4} lg={3}>
            <h4 className="footer-title">{t('contactInfo')}</h4>
            <div className="contact-item"><FaMapMarkerAlt /> <span>{t('address')}</span></div>
            <div className="contact-item"><FaPhone /> <a href="tel:+34614909102" style={{ color: 'inherit', textDecoration: 'none' }}>{t('phone')}</a></div>
            <div className="contact-item"><FaEnvelope /> <a href="mailto:info@flyvia.com" style={{ color: 'inherit', textDecoration: 'none' }}>{t('email')}</a></div>
            
            {/* <form className="newsletter-form border border-(var(--primary-color))" onSubmit={handleSubscribe}>
              <input 
                type="tel" 
                placeholder={isRTL ? "رقم الهاتف" : "Phone Number"} 
                className="newsletter-input" 
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
              <button type="submit" className="newsletter-btn !text-white">
                <FaPaperPlane />
              </button>
              {subscribed && <p style={{ color: '#4caf50', fontSize: '12px', marginTop: '10px' }}>{t('thankYou')}</p>}
            </form> */}
          </Col>
        </Row>
        
        <div className="footer-bottom">
          <div className="copyright">{t('copyright')}</div>
          <div className="legal-links">
            <a href="#">{t('privacy')}</a>
            <a href="#">{t('terms')}</a>
          </div>
        </div>
      </Container>
    </footer>
  );
};